import React from 'react';
import PropTypes from 'prop-types';

import { Wrapper, IconWrapper, LabelWrapper } from './index.styles';

import Badge from '../badge';
import Icon from '../icon';

/**
 * Renders a tabs item. Supports icons and badges.
 */
const TabsItem = React.forwardRef(
  (
    {
      as,
      badge,
      badgeColor = 'primary',
      badgeFaded,
      badgeSize = 'small',
      children,
      disabled,
      iconHeight = 16,
      iconSrc,
      iconWidth = 16,
      onClick,
      variant,
      ...otherProps
    },
    ref,
  ) => (
    <Wrapper
      {...otherProps}
      activeClassName="active"
      as={as}
      disabled={disabled}
      onClick={onClick}
      title={children}
      variant={variant}
      ref={ref}
    >
      {iconSrc && (
        <IconWrapper>
          <Icon alt="" src={iconSrc} width={iconWidth} height={iconHeight} inline />
        </IconWrapper>
      )}
      <LabelWrapper>{children}</LabelWrapper>
      {badge && (
        <Badge
          color={disabled ? 'coolGray' : badgeColor}
          faded={disabled || badgeFaded}
          size={badgeSize}
        >
          {badge}
        </Badge>
      )}
    </Wrapper>
  ),
);

TabsItem.displayName = 'TabsItem';

TabsItem.propTypes = {
  /**
   * Component that should render the tabs item. A common use case would be to write `as={NavLink}`
   * to use it with React-Router.
   */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  /**
   * Badge label.
   */
  badge: PropTypes.node,
  /**
   * Badge color.
   */
  badgeColor: PropTypes.oneOf(['blue', 'red', 'teal', 'coolGray']),
  /**
   * Whether badge background should be faded.
   */
  badgeFaded: PropTypes.bool,
  /**
   * Badge size.
   */
  badgeSize: PropTypes.oneOf(['regular', 'small']),
  /**
   * Tabs item label.
   */
  children: PropTypes.node,
  /**
   * Tabs item class name.
   */
  className: PropTypes.string,
  /**
   * Whether to disable the tabs item, provided that the component that renders it supports
   * disabled state (e.g. `'button'`).
   */
  disabled: PropTypes.bool,
  /**
   * Tabs item href. Only to be used when tabs item is rendered as a normal link.
   */
  href: PropTypes.string,
  /**
   * Icon height in pixels (recommended if icon URL is used).
   */
  iconHeight: PropTypes.number,
  /**
   * Icon, icon source or icon URL.
   */
  iconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Icon width in pixels (recommended if icon URL is used)
   */
  iconWidth: PropTypes.number,
  /**
   * Function to run on click.
   */
  onClick: PropTypes.func,
  /**
   * Tabs item to. Only to be used when tabs item is rendered as Link or NavLink.
   */
  to: PropTypes.string,
  /**
   * Tabs variant.
   */
  variant: PropTypes.oneOf(['filled', 'line']),
};

export default TabsItem;
